import {
  InputLabel,
  InteractiveCard,
  RadioField,
  RadioGroup,
  SingleSelect,
  SingleSelectChip,
} from '@pm/ui';
import { ComponentProps, ElementType, useId } from 'react';
import { useController } from 'react-hook-form';
import { errorMessage, formField, supportingMessage } from './FormField';

type Option =
  | ComponentProps<typeof RadioField>
  | ComponentProps<typeof SingleSelect>
  | ComponentProps<typeof InteractiveCard>
  | ComponentProps<typeof SingleSelectChip>;

type FormRadioGroupProps = Omit<
  ComponentProps<typeof RadioGroup>,
  'children'
> & {
  label?: React.ReactNode;
  name: string;
  options: Option[];
  supportingText?: string;
  freeTextDelimiter?: string;
  RadioComponent: ElementType;
};

const stripPrefixFromValue = ({
  prefix,
  value,
}: {
  prefix: string;
  value: string;
}) => {
  if (value.startsWith(prefix)) {
    return value.slice(prefix.length);
  }

  return '';
};

export const DEFAULT_FREE_TEXT_DELIMITER = ': '; // Shared with dataFormats/radioQuestionSchema

export const freeTextPrefixForOption = ({
  option,
  freeTextDelimiter,
}: {
  option: Option;
  freeTextDelimiter: string;
}) => `${option.value}${freeTextDelimiter}`;

/**
 * Component wrapper for radio group elements to be used with React Hook Forms.
 */
export const FormRadioGroup = ({
  label,
  name,
  options,
  supportingText,
  RadioComponent,
  space,
  onChange,
  orientation,
  required,
  freeTextDelimiter = DEFAULT_FREE_TEXT_DELIMITER,
  ...restProps
}: FormRadioGroupProps) => {
  const { field, fieldState } = useController({ name });
  const { error } = fieldState;
  const fieldValue: string = field.value ?? '';
  const generatedLabelId = useId();
  const labelId = label ? generatedLabelId : undefined;
  // As per design system specs, an error takes precedence over supporting text
  const showSupportingText = !error && supportingText;
  const errorId = error ? `${name}-error` : undefined;
  const supportingTextId = showSupportingText
    ? `${name}-description`
    : undefined;

  const optionIsSelected = ({
    fieldValue,
    option,
  }: {
    fieldValue: string;
    option: Option;
  }) => {
    if (option.freeText) {
      return fieldValue.startsWith(
        freeTextPrefixForOption({ option, freeTextDelimiter }),
      );
    }

    return option.value === fieldValue;
  };

  const selectedOption = options.find((option) =>
    optionIsSelected({ fieldValue, option }),
  );

  return (
    <div className={formField({ space })}>
      {label && (
        <InputLabel id={generatedLabelId} required={required}>
          {label}
        </InputLabel>
      )}
      <RadioGroup
        space={space}
        value={selectedOption?.value ?? null}
        aria-describedby={errorId || supportingTextId}
        aria-label={restProps['aria-label']}
        aria-labelledby={labelId ?? restProps['aria-labelledby']}
        onChange={onChange}
        orientation={orientation}
      >
        {options.map((option) => {
          const freeTextPrefix = freeTextPrefixForOption({
            option,
            freeTextDelimiter,
          });

          return (
            <RadioComponent
              {...field}
              {...option}
              key={`${name}-${option.value}`}
              onClick={() => {
                const updatedValue = option.freeText
                  ? freeTextPrefix
                  : option.value;
                field.onChange(updatedValue);
              }}
              freeText={
                option.freeText
                  ? {
                      ...option.freeText,
                      value: stripPrefixFromValue({
                        prefix: freeTextPrefix,
                        value: fieldValue,
                      }),
                      onChange: (
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const updatedValue = fieldValue.startsWith(
                          freeTextPrefix,
                        )
                          ? `${freeTextPrefix}${event.target.value}`
                          : '';

                        field.onChange(updatedValue);
                      },
                    }
                  : undefined
              }
              ref={null}
            />
          );
        })}
      </RadioGroup>
      {error && (
        <p id={errorId} className={errorMessage()}>
          {error.message}
        </p>
      )}
      {showSupportingText && (
        <p id={supportingTextId} className={supportingMessage()}>
          {supportingText}
        </p>
      )}
    </div>
  );
};
